import { useSetAtom } from "jotai";
import ConversationRecord from "models/view-models/conversations/conversation-record";
import { ConversationHistoryAtom } from "./conversation-history-atom";
import {
    BookviewConversationAtom,
    ModalConversationAtom,
    NotebookConversationAtom,
} from "../current-conversation/conversation-atoms";
import { useCallback } from "react";
import { addConversation } from "./add-conversation.thunk";
import { deleteConversation } from "./delete-conversation.thunk";
import { refreshConversationHistory } from "./refresh-conversation-history.thunk";
import { renameConversation } from "./rename-conversation.thunk";
import { pinOrUnpinConversation } from "utilities/atoms/conversations/conversation-history/pin-or-unpin-conversation.thunk";

export interface UseConversationActionsResult {
    /**
     * Adds a conversation to the history state.
     * @param conversation - The conversation to add.
     * @returns A thunk action to add the conversation.
     */
    addConversation: (conversation: ConversationRecord) => void;

    /**
     *  Clears the entire conversation history.
     *  This method will remove all conversations from the history state.
     */
    clearConversationHistory: () => void;

    /**
     * Deletes a conversation by its ID.
     * This method will update the state to remove the specified conversation.
     * @param conversationId - The ID of the conversation to delete.
     */
    deleteConversation(conversationId: string): void;

    /**
     * Refreshes the conversation history.
     * This method will update the state to reload the conversation history.
     */
    refreshConversationHistory(searchText?: string): void;

    /**
     * Renames an existing conversation.
     * This method will update the title of the current conversation and update the state accordingly.
     */
    renameConversation: (
        conversation: Pick<ConversationRecord, "id" | "title">
    ) => void;

    /**
     * Pins or unpins an existing conversation.
     * This method will update the pinned status of the current conversation and update the state accordingly.
     */
    pinOrUnpinConversation: (
        conversation: Pick<ConversationRecord, "id" | "isPinned">
    ) => void;
}

export function useConversationHistoryActions(): UseConversationActionsResult {
    const dispatch = useSetAtom(ConversationHistoryAtom);
    const modalChatDispatch = useSetAtom(ModalConversationAtom);
    const bookviewChatDispatch = useSetAtom(BookviewConversationAtom);
    const notebookChatDispatch = useSetAtom(NotebookConversationAtom);

    return {
        addConversation: useCallback(
            (conversation: ConversationRecord) => {
                dispatch(addConversation(conversation));
            },
            [dispatch]
        ),

        clearConversationHistory: useCallback(() => {
            dispatch({ type: "CLEAR_CONVERSATION_HISTORY" });
        }, [dispatch]),

        deleteConversation: useCallback(
            (conversationId: string) => {
                modalChatDispatch({
                    type: "CLEAR_CONVERSATION",
                    conversationId,
                });
                bookviewChatDispatch({
                    type: "CLEAR_CONVERSATION",
                    conversationId,
                });
                notebookChatDispatch({
                    type: "CLEAR_CONVERSATION",
                    conversationId,
                });
                dispatch(deleteConversation(conversationId));
            },
            [
                bookviewChatDispatch,
                dispatch,
                modalChatDispatch,
                notebookChatDispatch,
            ]
        ),

        refreshConversationHistory: useCallback(
            (searchText?: string) => {
                dispatch(refreshConversationHistory(searchText));
            },
            [dispatch]
        ),

        renameConversation: useCallback(
            (conversation: Pick<ConversationRecord, "id" | "title">) => {
                dispatch(renameConversation(conversation));
            },
            [dispatch]
        ),

        pinOrUnpinConversation: useCallback(
            (conversation: Pick<ConversationRecord, "id" | "isPinned">) => {
                dispatch(pinOrUnpinConversation(conversation));
            },
            [dispatch]
        ),
    };
}
